import { useState, useEffect, useCallback, useRef } from 'react';
import config from '../config';

// Shared setup status cache to avoid redundant API calls
let setupStatusCache = null;
let setupStatusPromise = null;
let lastFetchTime = 0;
const CACHE_DURATION = 30000; // 30 seconds cache

// Global state manager for setup status - notifies all hook instances
const setupStatusListeners = new Set();
const notifyAllListeners = (newStatus) => {
  console.log('📢 [SETUP STATUS] Notifying all listeners of status change:', newStatus?.isSetupComplete);
  setupStatusListeners.forEach(listener => listener(newStatus));
};

const useSetupStatus = () => {
  const [setupStatus, setSetupStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  const fetchSetupStatus = useCallback(async (forceRefresh = false) => {
    const now = Date.now();
    
    // Use cached data if available and not expired (unless force refresh)
    if (!forceRefresh && setupStatusCache && (now - lastFetchTime) < CACHE_DURATION) {
      console.log('🔄 [SETUP STATUS] Using cached data');
      if (mountedRef.current) {
        setSetupStatus(setupStatusCache);
        setIsLoading(false);
        setError(null);
      }
      return setupStatusCache;
    }
    
    // If there's already a fetch in progress, wait for it
    if (setupStatusPromise && !forceRefresh) {
      console.log('🔄 [SETUP STATUS] Waiting for existing fetch...');
      try {
        const result = await setupStatusPromise;
        if (mountedRef.current) {
          setSetupStatus(result);
          setIsLoading(false);
          setError(null);
        }
        return result;
      } catch (err) {
        if (mountedRef.current) {
          setError(err);
          setIsLoading(false);
        }
        return null;
      }
    }
    
    // Detect standalone client mode
    const isStandaloneClient = window.electronAPI && !window.location.href.includes('localhost');
    
    if (isStandaloneClient) {
      console.log('🎯 [SETUP STATUS] Standalone client mode - checking local setup status');
      
      // Check if setup is complete locally
      const localSetupComplete = localStorage.getItem('travelit_setup_complete') === 'true';
      const hasAdminUser = localStorage.getItem('travelit_admin_user') !== null;
      const isSetupComplete = localSetupComplete && hasAdminUser;
      
      console.log('📊 [SETUP STATUS] Local setup check:', { localSetupComplete, hasAdminUser, isSetupComplete });
      
      const offlineStatus = {
        isSetupComplete,
        data: { success: true, setupComplete: isSetupComplete, mode: 'standalone', source: 'local' }
      };
      
      setupStatusCache = offlineStatus;
      lastFetchTime = Date.now();
      
      if (mountedRef.current) {
        setSetupStatus(offlineStatus);
        setIsLoading(false);
        setError(null);
      }
      
      // Notify all listeners
      notifyAllListeners(offlineStatus);
      
      return offlineStatus;
    }
    
    console.log('🔄 [SETUP STATUS] Making fresh API call...');
    if (mountedRef.current) {
      setIsLoading(true);
      setError(null);
    }
    
    // Create new fetch promise
    setupStatusPromise = (async () => {
      try {
        const response = await fetch(`${config.apiUrl}/setup/status`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const data = await response.json();
        
        // Cache the result
        setupStatusCache = {
          isSetupComplete: data.success && data.setupComplete,
          data: data
        };
        lastFetchTime = Date.now();
        
        console.log('✅ [SETUP STATUS] Fetched and cached:', setupStatusCache);
        
        // Immediately notify all hook instances of the new status
        notifyAllListeners(setupStatusCache);
        
        return setupStatusCache;
        
      } catch (err) {
        console.error('❌ [SETUP STATUS] Fetch failed:', err);
        // Clear cache on error
        setupStatusCache = null;
        setupStatusPromise = null;
        throw err;
      } finally {
        // Clear promise after completion (success or failure)
        setupStatusPromise = null;
      }
    })();
    
    try {
      const result = await setupStatusPromise;
      if (mountedRef.current) {
        setSetupStatus(result);
        setIsLoading(false);
      }
      return result;
    } catch (err) {
      if (mountedRef.current) {
        setError(err);
        setSetupStatus(null);
        setIsLoading(false);
      }
      return null;
    }
  }, []);

  // Refresh setup status (invalidates cache)
  const refreshSetupStatus = useCallback(async () => {
    console.log('🔄 [SETUP STATUS] Force refresh requested');
    setupStatusCache = null;
    setupStatusPromise = null;
    lastFetchTime = 0;
    
    // Fetch new status and wait for it to complete
    const newStatus = await fetchSetupStatus(true);
    
    // Add a small delay to ensure all components have processed the state update
    await new Promise(resolve => setTimeout(resolve, 100));
    
    return newStatus;
  }, [fetchSetupStatus]);

  // Global listener for setup status changes
  useEffect(() => {
    const handleStatusUpdate = (newStatus) => {
      if (mountedRef.current) {
        console.log('🔔 [SETUP STATUS] Hook received status update:', newStatus?.isSetupComplete);
        setSetupStatus(newStatus);
        setIsLoading(false);
        setError(null);
      }
    };
    
    // Register this hook instance as a listener
    setupStatusListeners.add(handleStatusUpdate);
    
    return () => {
      setupStatusListeners.delete(handleStatusUpdate);
    };
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    mountedRef.current = true;
    fetchSetupStatus();
    
    return () => {
      mountedRef.current = false;
    };
  }, [fetchSetupStatus]);

  return {
    setupStatus,
    isLoading,
    error,
    refreshSetupStatus,
    isSetupComplete: setupStatus?.isSetupComplete || false
  };
};

// Export utility functions for manual cache management
export const clearSetupStatusCache = () => {
  console.log('🗑️ [SETUP STATUS] Cache cleared');
  setupStatusCache = null;
  setupStatusPromise = null;
  lastFetchTime = 0;
  
  // Notify all listeners that cache was cleared
  notifyAllListeners(null);
};

export const invalidateSetupStatusCache = () => {
  console.log('⏰ [SETUP STATUS] Cache invalidated');
  lastFetchTime = 0;
};

// Force update all hook instances with current cached status
export const forceUpdateAllInstances = () => {
  if (setupStatusCache) {
    console.log('🔄 [SETUP STATUS] Force updating all instances with cached status');
    notifyAllListeners(setupStatusCache);
  }
};

export default useSetupStatus;
